import { useState } from "react"
import { FolderKey, Loader2, MoreVertical, Pencil, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import type { Integration } from "@prescreen/lib/types"
import { cn } from "@prescreen/lib/utils"

function menuItemClass(disabled?: boolean) {
  return cn(
    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors",
    disabled
      ? "pointer-events-none cursor-not-allowed opacity-50"
      : "cursor-pointer hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent",
  )
}

export type IntegrationCardActionsMenuProps = {
  integration: Integration
  onEdit?: (integration: Integration) => void
  onManageSecrets?: (integration: Integration) => void
  onDelete?: (integration: Integration) => void
  isManagingSecrets?: boolean
  isDeleting?: boolean
}

export function IntegrationCardActionsMenu({
  integration,
  onEdit,
  onManageSecrets,
  onDelete,
  isManagingSecrets,
  isDeleting,
}: IntegrationCardActionsMenuProps) {
  const [open, setOpen] = useState(false)

  const showManage = Boolean(onManageSecrets)
  const showDelete = Boolean(onDelete)
  const manageDisabled = Boolean(isManagingSecrets)
  const deleteDisabled = Boolean(isDeleting)
  const triggerBusy = Boolean(isManagingSecrets || isDeleting)

  if (!onEdit && !showManage && !showDelete) {
    return null
  }

  const handleManage = () => {
    if (manageDisabled) {
      return
    }
    setOpen(false)
    void onManageSecrets?.(integration)
  }

  const handleEdit = () => {
    setOpen(false)
    onEdit?.(integration)
  }

  const handleDelete = () => {
    if (deleteDisabled) {
      return
    }
    setOpen(false)
    onDelete?.(integration)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 shrink-0 p-0"
          disabled={triggerBusy}
          aria-label="Integration actions"
        >
          {triggerBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <MoreVertical className="h-4 w-4" aria-hidden />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-56 p-1"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-0.5">
          {showManage ? (
            <button
              type="button"
              className={menuItemClass(manageDisabled)}
              onClick={handleManage}
              disabled={manageDisabled}
              aria-disabled={manageDisabled}
            >
              <FolderKey className="h-4 w-4 shrink-0" aria-hidden />
              Manage secrets
            </button>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              className={menuItemClass()}
              onClick={handleEdit}
            >
              <Pencil className="h-4 w-4 shrink-0" aria-hidden />
              Edit configuration
            </button>
          ) : null}
          {showDelete ? (
            <button
              type="button"
              className={cn(
                menuItemClass(deleteDisabled),
                !deleteDisabled && "text-destructive hover:text-destructive focus-visible:text-destructive",
              )}
              onClick={handleDelete}
              disabled={deleteDisabled}
              aria-disabled={deleteDisabled}
            >
              <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
              Delete integration
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
